import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import ErrorIcon from '@mui/icons-material/Error';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import BoltIcon from '@mui/icons-material/Bolt';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloudIcon from '@mui/icons-material/Cloud';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { Stack, Box } from '@mui/material';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Backdrop from '@mui/material/Backdrop';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Skeleton from '@mui/material/Skeleton';
import { ApiResourceFull } from '@shared/interfaces/ApiInterfaces';
import DeleteResourceDialog from 'components/DeleteResourceDialog';
import CancelRequestDialog from 'components/CancelRequestDialog';
import PipelineActionDialog from 'components/PipelineActionDialog';
import RequestStatusChip from 'components/RequestStatusChip';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import BackendApiService from '../services/BackendApiService';
import { getAccessToken } from '../utils/accessTokenStore';
import { MultiError } from '../utils/errors';
import { transformLocation } from '../utils/utils';

function UserRequestDetailPage() {
  const { id } = useParams() as { id: string };
  const navigate = useNavigate();
  const [accessToken] = useState(() => {
    return getAccessToken();
  });
  const [data, setData] = useState<ApiResourceFull>();
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [locationNameToRegion, setLocationNameToRegion] = useState<Record<string, string>>({});

  const getStatusDescription = (checks: any[], showFailedStatus: boolean = true) => {
    if (!checks) {
      return "";
    }
    const numberChecks = checks?.length;
    if (!numberChecks) {
      return "";
    }
    const failedChecks = checks?.filter((c: any) => c.state === "failed" || c.state === "error")?.length || 0;
    if (failedChecks > 0) {
      return `${failedChecks}/${numberChecks} checks${showFailedStatus ? " failed" : ""}`;
    }
    const succeededChecks = checks?.filter((c: any) => c.state === "succeeded")?.length || 0;
    return `${succeededChecks}/${numberChecks} checks successful`;
  };

  const getApprovalDescription = (reviewers: any[]) => {
    if (!reviewers) {
      return "";
    }
    const numberReviewers = reviewers.length;
    const numberReviewersRejected = reviewers.filter((r: any) => r.vote < 0).length;
    const numberReviewersApproved = reviewers.filter((r: any) => r.vote > 0).length;
    if (numberReviewersRejected > 0) {
      return `${numberReviewersRejected}/${numberReviewers} reviewers have rejected`;
    }
    return `${numberReviewersApproved}/${numberReviewers} reviewers have approved`;
  };

  const renderStepper = (data: ApiResourceFull) => {
    // If PR is completed or resource is provisioned, validation and approval must have succeeded
    const isCompleted = data.prStatus === "completed";
    const isProvisioned = data.checks?.apply?.status && data.checks.apply.status !== "pending" && data.checks.apply.status !== "unknown";
    
    return [
      {
        title: 'Request created',
        description: data.prStatus === "abandoned" ? "Request was cancelled" : "",
        status: !data.prStatus ? "unknown" : data?.prStatus === "abandoned" ? "error" : "success",
      },
      {
        title: 'Resource validated',
        description: getStatusDescription(data.checks?.validation?.checks),
        status: (isCompleted || isProvisioned) ? "success" : data.checks?.validation?.status,
      },
      {
        title: 'Approval',
        description: getApprovalDescription(data.checks?.review?.reviewers),
        status: (isCompleted || isProvisioned) ? "success" : (!data.checks?.review?.status ? "unknown" : data.checks?.review?.status === "approved" ? "success" : data.checks?.review?.status === "pending" ? "pending" : "error"),
      },
      {
        title: 'Request completed',
        description: "",
        status: data.prStatus === "completed" ? "success" : "pending",
      },
      {
        title: 'Resource provisioned',
        description: getStatusDescription(data.checks?.apply?.checks, false),
        status: !data.checks?.apply?.status ? "unknown" : data.checks?.apply?.status === "pending" ? data.checks?.apply?.status : "success",
      }
    ];
  };

  const renderDeleteStepper = (data: NonNullable<ApiResourceFull["deleteRequest"]>) => {
    // If delete PR is completed or delete is finished, validation and approval must have succeeded
    const isCompleted = data.prStatus === "completed";
    const isFinished = data.checks?.apply?.status && data.checks.apply.status !== "pending" && data.checks.apply.status !== "unknown";
    
    return [
      {
        title: 'Resource provisioned',
        description: "",
        status: "success",
      },
      {
        title: 'Delete request created',
        description: "",
        status: !data.prStatus ? "unknown" : "success",
      },
      {
        title: 'Delete validated',
        description: getStatusDescription(data.checks?.validation?.checks),
        status: (isCompleted || isFinished) ? "success" : data.checks?.validation?.status,
      },
      {
        title: 'Delete approved',
        description: getApprovalDescription(data.checks?.review?.reviewers),
        status: (isCompleted || isFinished) ? "success" : (!data.checks?.review?.status ? "unknown" : data.checks?.review?.status === "approved" ? "success" : data.checks?.review?.status === "pending" ? "pending" : "error"),
      },
      {
        title: 'Delete request completed',
        description: "",
        status: data.prStatus === "completed" ? "success" : "pending",
      },
      {
        title: 'Delete finished',
        description: getStatusDescription(data.checks?.apply?.checks),
        status: data.checks?.apply?.status === "pending" ? data.checks?.apply?.status : "success",
      }
    ];
  };

  const [currentStep, setCurrentStep] = useState<number>(0);
  const [steps, setSteps] = useState<any[]>(renderStepper({} as any));
  const [loading, setLoading] = useState<boolean>(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState<boolean>(false);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  const [pipelineActionDialog, setPipelineActionDialog] = useState<{ open: boolean; action: any }>({ open: false, action: null });
  const [filterText, setFilterText] = useState<string>('');
  const hasModules = (data?.modules?.length || 0) > 0;

  const getEmptyDetailsMessage = (): string => {
    const statusDescription = data?.statusDescription?.trim();
    if (statusDescription) {
      return statusDescription;
    }

    if (data && !data.isEditAllowed) {
      return `Currently locked by ${data.requester || 'another user'}.`;
    }

    return 'No request details are available at the moment.';
  };

  useEffect(() => {
    const prepareTFModule = async () => {
      setLoading(true);
      try {
        const backendApiService = new BackendApiService(accessToken ?? '');
        const [data, locations] = await Promise.all([
          backendApiService.getUserRequest(id),
          backendApiService.getAzureLocations({}),
        ]);
        setData(data);
        setLocationNameToRegion(
          locations.reduce((acc: Record<string, string>, location) => {
            acc[location.name] = location.region;
            return acc;
          }, {})
        );

        // Build stepper
        let renderedSteps;
        if (data.deleteRequest) {
          renderedSteps = renderDeleteStepper(data.deleteRequest);
        } else {
          renderedSteps = renderStepper(data);
        }
        const activeStepIndex = renderedSteps.findIndex(s => s.status !== "success");
        // If all steps are successful (activeStepIndex === -1), set currentStep to length to gray out all steps
        setCurrentStep(activeStepIndex === -1 ? renderedSteps.length : activeStepIndex);
        setSteps(renderedSteps);
      } catch (error) {
        console.log(error);
        if (error instanceof MultiError) {
          setErrorMessage(error.errors.join(', '));
        } else if (error instanceof Error) {
          setErrorMessage(error.message);
        } else {
          setErrorMessage((error as any).toString());
        }
      }
      setLoading(false);
    };

    prepareTFModule();
  }, [accessToken, id]);

  const getResourceIcon = (platform: string, resourceName: string) => {
    // Build resource-specific icon path, e.g., azure-vm.svg, vsphere-vm.svg
    const iconPath = `/icons/${platform.toLowerCase()}-${resourceName.toLowerCase()}.svg`;
    return iconPath;
  };

  const getAzureDevOpsFileLink = (module: any) => {
    if (!module.id) return null;
    
    // Extract SSR number from module.id
    const ssrNumber = module.id;
    
    // Get repository from module data, fallback to 'azure-compute' for backwards compatibility
    const repository = module.repositoryName || 'azure-compute';
    
    // Build path dynamically from path pattern, replacing mustache variables
    let basePath = module.path || '';
    
    // Replace mustache variables with values from module.data
    if (module.data) {

      const location = module.data.location || '';
      const mappedLocation = transformLocation(location, locationNameToRegion);
      
      // Common replacements
      basePath = basePath.replace(/{{\s*subscription\s*}}/g, module.data.subscription || '');
      basePath = basePath.replace(/{{\s*location\s*}}/g, mappedLocation);
      basePath = basePath.replace(/{{\s*subscription_china\s*}}/g, module.data.subscription_china || '');
      basePath = basePath.replace(/{{\s*organization\s*}}/g, module.data.organization || '');
    }
    
    // Build full path: /{basePath}/self-service/SSR.../SSR....tf
    // Handle empty basePath (e.g., for identity modules)
    const fullPath = basePath 
      ? `/${basePath}/self-service/${ssrNumber}/${ssrNumber}.tf`
      : `/self-service/${ssrNumber}/${ssrNumber}.tf`;
    
    return `https://dev.azure.com/wacprod/WackerAutomation/_git/${repository}?path=${fullPath}`;
  };

  const getAzureDevOpsPrLink = (module: any, prId: string) => {
    if (!module || !prId) return null;
    
    // Get repository from module data, fallback to 'azure-compute' for backwards compatibility
    const repository = module.repositoryName || 'azure-compute';
    
    return `https://dev.azure.com/wacprod/WackerAutomation/_git/${repository}/pullrequest/${prId}`;
  };

  const getServiceNowLink = (snowRef: string) => {
    if (!snowRef || !snowRef.startsWith('SCTASK')) return null;
    // Direct link to ServiceNow task using sysparm_query
    return `https://wacker.service-now.com/sc_task.do?sysparm_query=number=${snowRef}`;
  };

  const renderValue = (value: any): React.ReactNode => {
    if (value === null || value === undefined) return <Typography variant="body2" color="text.secondary">-</Typography>;
    if (typeof value === 'boolean') return <Chip label={value ? 'true' : 'false'} size="small" color='default' />;
    if (typeof value === 'string' || typeof value === 'number') {
      const strValue = String(value);
      return <Typography variant="body2">{strValue}</Typography>;
    }
    return null;
  };

  const matchesFilter = (key: string, value: any, searchText: string): boolean => {
    if (!searchText) return true;
    const search = searchText.toLowerCase();
    
    // Check if key matches
    if (key.toLowerCase().includes(search)) return true;
    
    // Check if value matches (for primitive values)
    if (value !== null && value !== undefined) {
      const valueStr = String(value).toLowerCase();
      if (valueStr.includes(search)) return true;
    }
    
    // Check nested objects/arrays recursively
    if (value && typeof value === 'object') {
      return Object.entries(value).some(([nestedKey, nestedValue]) => 
        matchesFilter(nestedKey, nestedValue, searchText)
      );
    }
    
    return false;
  };

  const renderNestedData = (data: any, parentRequired: boolean = true, level: number = 0): React.ReactNode => {
    if (!data || (data.constructor !== Array && data.constructor !== Object)) {
      return renderValue(data);
    }

    const isArray = Array.isArray(data);
    const entries = Object.keys(data);
    
    // Filter entries based on search text
    const filteredEntries = filterText 
      ? entries.filter(key => {
          const item = data[key];
          const value = item?.Required || item?.Optional || item;
          return matchesFilter(key, value, filterText);
        })
      : entries;
    
    if (filteredEntries.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', py: 2 }}>
          No matching fields found
        </Typography>
      );
    }
    
    const hasNestedObjects = filteredEntries.some(key => {
      const value = data[key]?.Required || data[key]?.Optional || data[key];
      return value && (value.constructor === Object || value.constructor === Array);
    });

    if (hasNestedObjects || level > 0) {
      return (
        <Box sx={{ ml: level > 0 ? 2 : 0 }}>
          {filteredEntries.map((key) => {
            const item = data[key];
            const isRequired = !!item?.Required;
            const isOptional = !!item?.Optional;
            const value = item?.Required || item?.Optional || item;
            const isNested = value && (value.constructor === Object || value.constructor === Array);
            const cleanKey = key.replaceAll("<map>", "");

            if (isNested) {
              return (
                <Accordion key={key} sx={{ mb: 1, '&:before': { display: 'none' } }} elevation={0}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: 'grey.50' }}>
                    <Typography variant="body2" sx={{ fontWeight: isRequired ? 600 : 400 }}>
                      {isArray ? `Item ${parseInt(key) + 1}` : cleanKey}
                      {isOptional && <Chip label="Optional" size="small" sx={{ ml: 1, height: 20 }} />}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ bgcolor: 'background.paper' }}>
                    {renderNestedData(value, isRequired, level + 1)}
                  </AccordionDetails>
                </Accordion>
              );
            }

            // For arrays, don't show the index as a label - just show the value
            if (isArray) {
              return (
                <Box key={key} sx={{ py: 1, borderBottom: 1, borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
                  {renderValue(value)}
                </Box>
              );
            }

            return (
              <Box key={key} sx={{ py: 1, borderBottom: 1, borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: isOptional ? 'text.secondary' : 'text.primary' }}>
                      {cleanKey}
                      {isOptional && <Chip label="Optional" size="small" sx={{ ml: 1, height: 20 }} />}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    {renderValue(value)}
                  </Grid>
                </Grid>
              </Box>
            );
          })}
        </Box>
      );
    }

    return (
      <Box>
        {filteredEntries.map((key) => {
          const item = data[key];
          const isRequired = !!item?.Required;
          const isOptional = !!item?.Optional;
          const value = item?.Required || item?.Optional || item;
          const cleanKey = key.replaceAll("<map>", "");

          // For arrays, don't show the index as a label - just show the value
          if (isArray) {
            return (
              <Box key={key} sx={{ py: 1, borderBottom: 1, borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
                {renderValue(value)}
              </Box>
            );
          }

          return (
            <Box key={key} sx={{ py: 1, borderBottom: 1, borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: isOptional ? 'text.secondary' : 'text.primary' }}>
                    {cleanKey}
                    {isOptional && <Chip label="Optional" size="small" sx={{ ml: 1, height: 20 }} />}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={8}>
                  {renderValue(value)}
                </Grid>
              </Grid>
            </Box>
          );
        })}
      </Box>
    );
  }

  return (
    <div className="content-surface">
      {errorMessage ? (
        <Alert severity="error">
          <AlertTitle>Error loading request</AlertTitle>
          {errorMessage}
        </Alert>
      ) : (
        <div>
          <Box className="surface-header-strip" sx={{ mb: 3.2 }}>
            <Box className="surface-header-divider-line">
              <Grid container alignItems="center">
                <Grid item xs={8}>
                  <Stack direction="row" alignItems="center">
                    <h2 className="page-title" style={{ marginRight: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      Your Request: #{id}
                      {data?.modules?.[0] && getAzureDevOpsPrLink(data.modules[0], id) && (
                        <IconButton 
                          size="small" 
                          onClick={() => {
                            const link = getAzureDevOpsPrLink(data.modules[0], id);
                            if (link) window.open(link, '_blank');
                          }}
                          sx={{ padding: 0.5 }}
                        >
                          <OpenInNewIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      )}
                    </h2>
                    {data && (
                      <RequestStatusChip
                        status={data.status}
                        type={data.statusType}
                        description={data.statusDescription}
                        onClick={(() => {
                          const link = data.modules?.[0] && getAzureDevOpsPrLink(data.modules[0], id);
                          return link ? () => window.open(link, '_blank', 'noopener,noreferrer') : undefined;
                        })()}
                        trailingIcon={data.modules?.[0]?.fileFromMainFallback ? (
                          <span style={{ fontSize: '0.7rem', color: '#fff', cursor: 'default' }}>*</span>
                        ) : undefined}
                      />
                    )}
                  </Stack>
                </Grid>
                <Grid container item xs={4} justifyContent="flex-end">
                  <Grid item>
                    <ButtonGroup variant="outlined" size="medium">
                  {/* Edit Button */}
                  <Tooltip title={!data?.isEditAllowed ? "Edit not allowed in this status" : data?.modules?.[0]?.fileNotTracked ? "File not tracked - edit not allowed" : ""}>
                    <span>
                      <Button
                        component={Link}
                        to={`/module/${data?.modules?.[0]?.platform}/${data?.modules?.[0]?.name}/${id}`}
                        disabled={!data?.isEditAllowed || data?.modules?.[0]?.fileNotTracked}
                        startIcon={<EditIcon />}
                        sx={{ minWidth: '100px' }}
                      >
                        Edit
                      </Button>
                    </span>
                  </Tooltip>

                  {/* Actions Menu - nur anzeigen wenn Pipeline Actions vorhanden */}
                  {(() => {
                    const hasPipelineActions = data?.modules?.[0]?.pipelineActions && data.modules[0].pipelineActions.length > 0;
                    if (!hasPipelineActions || !data?.modules?.[0]) return null;
                    
                    return (
                      <>
                        <Button
                          onClick={(e) => setActionMenuAnchor(e.currentTarget)}
                          startIcon={<BoltIcon />}
                          color="success"
                          disabled={data?.modules?.[0]?.fileNotTracked}
                          sx={{ minWidth: '110px' }}
                        >
                          Actions
                        </Button>
                        <Menu
                          anchorEl={actionMenuAnchor}
                          open={Boolean(actionMenuAnchor)}
                          onClose={() => setActionMenuAnchor(null)}
                          anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                          }}
                          transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                          }}
                        >
                          <MenuList dense sx={{ paddingY: 0 }}>
                            {data.modules[0].pipelineActions!.map((action: any) => (
                              <MenuItem
                                key={action.name}
                                onClick={() => {
                                  setActionMenuAnchor(null);
                                  setPipelineActionDialog({ open: true, action });
                                }}
                                disabled={data?.status === "cancelled" || data?.modules?.[0]?.fileNotTracked}
                              >
                                <ListItemIcon>
                                  <BoltIcon fontSize="small" />
                                </ListItemIcon>
                                {action.name}
                              </MenuItem>
                            ))}
                          </MenuList>
                        </Menu>
                      </>
                    );
                  })()}

                  {/* Delete / Cancel Button */}
                  {data?.prStatus === "active" ? (
                    <Tooltip title="">
                      <span>
                        <Button
                          onClick={() => setCancelDialogOpen(true)}
                          startIcon={<CancelIcon />}
                          color="warning"
                          sx={{ minWidth: '100px' }}
                        >
                          Cancel
                        </Button>
                      </span>
                    </Tooltip>
                  ) : (
                    <Tooltip title={data?.statusType === "delete" || data?.status === "cancelled" ? "Delete not allowed in this status" : data?.modules?.[0]?.fileNotTracked ? "File not tracked - delete not allowed" : ""}>
                      <span>
                        <Button
                          onClick={() => setDeleteDialogOpen(true)}
                          disabled={data?.statusType === "delete" || data?.status === "cancelled" || data?.modules?.[0]?.fileNotTracked}
                          startIcon={<DeleteIcon />}
                          color="error"
                          sx={{ minWidth: '100px' }}
                        >
                          Delete
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                    </ButtonGroup>
                  </Grid>
                </Grid>
              </Grid>
            </Box>

            <Stepper activeStep={currentStep} sx={{ pt: 3 }}>
            {steps.map((step: any, index: number) => {
              let Icon = RadioButtonUncheckedIcon;
              const stepProps: { completed?: boolean } = {};
              const labelProps: {
                optional?: React.ReactNode;
                error?: boolean;
              } = {};
              if (step.status === "error") {
                labelProps.error = true;
                Icon = ErrorIcon;
              } else if (step.status === "success") {
                Icon = CheckCircleIcon;
              } else if (currentStep === index) {
                Icon = AccessTimeIcon;
              } else if (index > currentStep) {
                Icon = RadioButtonUncheckedIcon;
              }
              
              // Set completed status based on step position relative to currentStep
              if (index < currentStep) {
                stepProps.completed = true;
              } else if (index > currentStep) {
                stepProps.completed = false;
              }
              labelProps.optional = (
                <Typography variant="caption" color={
                  stepProps.completed === true ? "grey.500" : 
                  stepProps.completed === false ? "grey.700" : 
                  labelProps.error ? "error" : (
                  step.status === "success" ? "success.main" : "primary.dark"
                )}>
                  {step.description}
                </Typography>
              );

              return (
                <Step key={index} {...stepProps}>
                  <StepLabel
                    {...labelProps}
                    icon={<Icon color={
                      stepProps.completed === true ? "disabled" :
                      stepProps.completed === false ? "disabled" : 
                      labelProps.error ? "error" : (
                      step.status === "success" ? "success" : "primary"
                    )} />}
                  >
                    {step.title}
                  </StepLabel>
                </Step>
              );
            })}
            </Stepper>
          </Box>

          <Box sx={{ marginTop: '20px !important', marginLeft: '20px !important', marginRight: '20px !important' }}>
            {hasModules ? (
              <>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Filter fields..."
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                  sx={{ mb: 3 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                      </InputAdornment>
                    )
                  }}
                />
                {data?.modules.map((module: any, index: number) => (
                  <Card key={index} sx={{ mb: 3, border: '1px solid #e6dddd', boxShadow: 'none', borderRadius: 2 }} elevation={0}>
                    <CardHeader
                      avatar={
                        <img
                          src={getResourceIcon(module.platform, module.name)}
                          alt={module.platform}
                          width={24}
                          height={24}
                          style={{ objectFit: 'contain' }}
                        />
                      }
                      title={
                        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                          <span style={{ textTransform: 'capitalize' }}>{module.platform}</span> - {module.title}
                        </Typography>
                      }
                      subheader={
                        <Box sx={{ paddingTop: '10px', paddingLeft: '24px' }}>
                          <Typography component="div" variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            Module Reference: <strong>{module.id}</strong>
                            {!module.fileNotTracked && getAzureDevOpsFileLink(module) && (
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const link = getAzureDevOpsFileLink(module);
                                  if (link) window.open(link, '_blank');
                                }}
                                sx={{ padding: 0.5 }}
                              >
                                <OpenInNewIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            )}
                            {module.fileNotTracked && (
                              <Chip
                                label="File not tracked"
                                size="small"
                                color="warning"
                                sx={{ fontSize: '0.7rem', height: 22, ml: 1 }}
                              />
                            )}
                          </Typography>
                          <Typography component="div" variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            SNOW Reference: <strong>{data.snowRef}</strong>
                            {!data.snowRef?.startsWith('SCTASK') && data.snowRef && (
                              <Chip
                                label="SCTASK not tracked"
                                size="small"
                                color="warning"
                                sx={{ fontSize: '0.7rem', height: 22, ml: 1 }}
                              />
                            )}
                            {getServiceNowLink(data.snowRef) && (
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const link = getServiceNowLink(data.snowRef);
                                  if (link) window.open(link, '_blank');
                                }}
                                sx={{ padding: 0.5 }}
                              >
                                <OpenInNewIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            )}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Requested by: <strong>{data.requester}</strong>
                          </Typography>
                        </Box>
                      }
                      sx={{ bgcolor: 'grey.50' }}
                    />
                    <Divider />
                    <CardContent>
                      {renderNestedData(module?.data, true, 0)}
                    </CardContent>
                  </Card>
                ))}
              </>
            ) : data ? (
              <Alert severity={data && !data.isEditAllowed ? 'warning' : 'info'} sx={{ mb: 2 }}>
                <AlertTitle>{data && !data.isEditAllowed ? 'Request currently locked' : 'No details available'}</AlertTitle>
                {getEmptyDetailsMessage()}
              </Alert>
            ) : (
              <>
                <Skeleton variant="rounded" height={40} sx={{ mb: 3 }} />
                <Card sx={{ mb: 3, border: '1px solid #dfd7d7', boxShadow: 'none', borderRadius: 1.5 }} elevation={0}>
                  <CardHeader
                    title={<Skeleton variant="text" width="34%" height={30} />}
                    subheader={
                      <Box sx={{ pt: 0.6 }}>
                        <Skeleton variant="text" width="42%" height={60} />
                      </Box>
                    }
                    sx={{ bgcolor: 'grey.50' }}
                  />
                  <Divider />
                  <CardContent>
                    <Box sx={{ display: 'grid', gap: 1.5 }}>
                      <Skeleton variant="rounded" height={46} />
                      <Skeleton variant="rounded" height={46} />
                      <Skeleton variant="rounded" height={46} width="88%" />
                    </Box>
                  </CardContent>
                </Card>
              </>
            )}
          </Box>

          {data?.modules?.[0] && (
            <>
              <DeleteResourceDialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                requestId={id}
                module={{
                  platform: data.modules[0].platform || "",
                  name: data.modules[0].name || "",
                  id: data.modules[0].id,
                  resourceName: data.modules[0].title || data.modules[0].name || "",
                }}
                onSuccess={() => {
                  setDeleteDialogOpen(false);
                  navigate('/user/requests');
                }}
              />

              <CancelRequestDialog
                open={cancelDialogOpen}
                onClose={() => setCancelDialogOpen(false)}
                requestId={id}
                platform={data.modules[0].platform || ""}
                name={data.modules[0].name || ""}
                resourceName={data.modules[0].title || data.modules[0].name || ""}
                onSuccess={() => {
                  setCancelDialogOpen(false);
                  navigate('/user/requests');
                }}
              />
              
              {pipelineActionDialog.action && (
                <PipelineActionDialog
                  open={pipelineActionDialog.open}
                  onClose={() => setPipelineActionDialog({ open: false, action: null })}
                  action={pipelineActionDialog.action}
                  request={{
                    ...data,
                    pullRequestId: parseInt(id),
                    resourceName: data.modules[0].id?.split('/')?.[1] || data.modules[0].id,
                    name: data.modules[0].name,
                    platform: data.modules[0].platform,
                    title: data.modules[0].title
                  }}
                />
              )}
            </>
          )}
        </div>
      )}

      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </div>
  );
}

export default UserRequestDetailPage;
